import CircularProgress from "@material-ui/core/CircularProgress";
import Typography from "@material-ui/core/Typography";
import React, { FC, useCallback, useState } from "react";
import { Theme } from "@material-ui/core/styles";
import { styled } from "@material-ui/styles";
import { useSelector } from "react-redux";

import DropZone from "../../../../components/elements/DropZone";
import HubSpotSelectorRedesign from "../../../../components/CampaignCreation/HubSpotSelector/HubSpotSelectorRedesign";
import { ReduxApplicationState } from "../../../../types/ReduxTypes/ReduxApplicationState";
import { SelectOptionType } from "../../../../components/elements/Select/Select";
import { errorToaster } from "../../../../helpers/toasters";
import { validateHubSpotData } from "../../../../helpers/validateHubSpotData";

type Props = {
  processValidatedData: (data: string[], fileName: string) => void;
  regex: RegExp;
  hubSpotSelectorOptions: SelectOptionType[];
  getDataFromHubSpot: () => void;
  handleOptionChange: (e: any) => void;
  hubSpotSelectorValue: any;
  areDataFromHubSpotIsLoading: boolean;
  sampleLink: string;
};

const LoadingState = styled("div")({
  flex: 1,
  minHeight: 240,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

const EmptyText = styled(Typography)(({ theme }: { theme: Theme }) => ({
  fontSize: 13,
  color: theme.palette.brand.gray500,
  marginBottom: 16,
}));

const OrDivider = styled("div")(({ theme }: { theme: Theme }) => ({
  display: "flex",
  alignItems: "center",
  margin: "28px 0",
  gap: 12,
  color: theme.palette.brand.gray500,
  fontSize: 13,
  "&::before, &::after": {
    content: '""',
    flex: 1,
    height: 1,
    background: theme.palette.brand.gray200,
  },
}));

const UploadTitle = styled(Typography)(({ theme }: { theme: Theme }) => ({
  fontSize: 15,
  fontWeight: 600,
  color: theme.palette.brand.gray900,
  marginBottom: 12,
}));

const HubSpotUploaderRedesign: FC<Props> = ({
  processValidatedData,
  regex,
  hubSpotSelectorOptions,
  getDataFromHubSpot,
  handleOptionChange,
  hubSpotSelectorValue,
  areDataFromHubSpotIsLoading,
  sampleLink,
}) => {
  const [didErrorOccurInUpload, setUploadError] = useState(false);

  const isHubSpotDataPending = useSelector((state: ReduxApplicationState) => state.campaignToCreate.isPending);
  const selectedAdvertiser = useSelector((state: ReduxApplicationState) => state.campaignToCreate.selectedAdvertiser);

  const onDrop = useCallback(
    (acceptedFiles: any[]) => {
      setUploadError(false);
      acceptedFiles.forEach(async (file: any) => {
        try {
          const reader = new FileReader();
          reader.readAsText(file);
          reader.addEventListener("load", (event) => {
            const csv = event.target!.result as string;
            const rows = csv.split("\n").map((row) => row.replace(/\r/g, ""));
            const { validatedData, errorMessage } = validateHubSpotData(rows, regex, "Emails");
            if (validatedData.length > 0) {
              processValidatedData(validatedData, file.name);
            }
            if (errorMessage) errorToaster(errorMessage, 10000);
          });
        } catch (error) {
          setUploadError(true);
          console.error("File upload error:", error);
        }
      });
    },
    [regex, processValidatedData],
  );

  if (isHubSpotDataPending) {
    return (
      <LoadingState>
        <CircularProgress size={32} />
      </LoadingState>
    );
  }

  return (
    <div>
      {hubSpotSelectorOptions?.length > 0 ? (
        <HubSpotSelectorRedesign
          label="Select HubSpot Segments"
          options={hubSpotSelectorOptions}
          value={hubSpotSelectorValue}
          onChange={handleOptionChange}
          onButtonClick={getDataFromHubSpot}
          buttonLabel="Get Emails"
          disabled={areDataFromHubSpotIsLoading}
        />
      ) : (
        <EmptyText>
          {selectedAdvertiser?.hubspot_authed
            ? "No HubSpot Segments found linked to this advertiser's HubSpot Account."
            : "HubSpot is not connected for this advertiser. Connect HubSpot in the advertiser settings to use contact lists."}
        </EmptyText>
      )}

      {!areDataFromHubSpotIsLoading && (
        <>
          {selectedAdvertiser?.hubspot_authed ? <OrDivider>OR</OrDivider> : <div style={{ marginTop: 28 }} />}
          <UploadTitle>Upload Emails via CSV</UploadTitle>
          <DropZone
            explanatoryText="Upload a CSV file with one column of Emails. Each entry should be on a new line."
            sampleLink={sampleLink}
            onDrop={onDrop}
            didErrorOccurInUpload={didErrorOccurInUpload}
          />
        </>
      )}
    </div>
  );
};

export default HubSpotUploaderRedesign;
